import TrackArtistCredits from "./TrackArtistCredits";
import BpmInput from "../ui/BpmInput";
import SetTrackArtists from "./SetTrackArtists";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import Alert from "@mui/material/Alert";
import MenuSurface from "../ui/MenuSurface";
import MenuItem from "@mui/material/MenuItem";
import useEditorForm from "../hooks/useEditorForm";
import TextInput from "../ui/TextInput";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import CloseIcon from "@mui/icons-material/Close";
import Button from "@mui/material/Button";
import React from "react";
import { API_BASE, resolveBpmBadge } from "../utils";
import { CSS } from "@dnd-kit/utilities";
import useAsyncAction from "../hooks/useAsyncAction";
import { describeApiError } from "../api/request";
export default function SetTrackCard({
  setTrack,
  onNavigate,
  onUpdate,
  onRemove,
  attributes = {},
  listeners = {},
  setNodeRef,
  transform,
  transition,
  isDragging,
}) {
  const [menuAnchor, setMenuAnchor] = React.useState(null);
  const action = useAsyncAction();
  const track = setTrack.track || setTrack;
  const title = track.title || track.name || "";
  const albumId =
    track.album || track.album_id || setTrack.album || setTrack.album_id;
  const [menuOpen, setMenuOpen] = React.useState(false);
  const [editing, setEditing] = React.useState(false);
  const [fields, setFields, editorForm] = useEditorForm({
    bpm: setTrack.bpm ?? track.bpm ?? "",
    duration: setTrack.duration ?? track.duration ?? "",
    notes: setTrack.notes || track.notes || "",
  });
  const bpmFallback =
    track.default_bpm !== null && track.default_bpm !== undefined
      ? track.default_bpm
      : track.bpm;
  const bpmInfo = resolveBpmBadge(fields.bpm, bpmFallback);
  const hasBpm = bpmInfo.hasResolved && bpmInfo.hasBadge;
  React.useEffect(() => {
    if (editing) return;
    setFields({
      bpm: setTrack.bpm ?? track.bpm ?? "",
      duration: setTrack.duration ?? track.duration ?? "",
      notes: setTrack.notes || track.notes || "",
    });
  }, [setTrack, track, editing, setFields]);
  const handleChange = (field, value) => {
    setFields((f) => ({
      ...f,
      [field]: value,
    }));
  };
  const handleSave = async () => {
    const update = {
      bpm: fields.bpm === "" ? null : fields.bpm,
      duration: fields.duration,
      notes: fields.notes,
    };
    if (await action.run(() => onUpdate(setTrack.id, update)))
      setEditing(false);
  };
  const handleCancel = () => {
    action.clearError();
    setFields({
      bpm: setTrack.bpm ?? track.bpm ?? "",
      duration: setTrack.duration ?? track.duration ?? "",
      notes: setTrack.notes || track.notes || "",
    });
    setEditing(false);
  };
  return (
    <div
      className={`track-card set-track-card${editing ? " editing" : ""}`}
      ref={setNodeRef}
      aria-busy={action.pending}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
      }}
    >
      {!editing && (
        <IconButton
          className="drag-corner"
          title="Drag to reorder"
          aria-label="Drag to reorder"
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            zIndex: 1,
          }}
          {...listeners}
          {...attributes}
        >
          <DragIndicatorIcon />
        </IconButton>
      )}
      {albumId && (
        <div className="set-track-image-wrapper">
          <img
            className="set-track-image"
            src={`${API_BASE}/albums/${albumId}/image/`}
            alt="album art"
            width={100}
            height={100}
          />
          {hasBpm && (
            <span className={`bpm-badge ${bpmInfo.badgeClass}`.trim()}>
              {bpmInfo.displayValue}
            </span>
          )}
        </div>
      )}
      <div className="card-content">
        <div className="track-header">
          <div className="track-title-group">
            <Button
              type="button"
              className="track-title"
              variant="text"
              onClick={onNavigate}
            >
              {title}
            </Button>
            <SetTrackArtists track={track} />
            <TrackArtistCredits track={track} />
          </div>
          {editing ? (
            <IconButton
              type="button"
              className="close-edit"
              onClick={handleCancel}
              aria-label="Cancel edit"
              disabled={action.pending}
            >
              <CloseIcon />
            </IconButton>
          ) : (
            <div
              className="menu"
              style={{
                marginLeft: "auto",
              }}
            >
              <IconButton
                type="button"
                className="menu-toggle"
                onClick={(event) => {
                  setMenuAnchor(event.currentTarget);
                  setMenuOpen((o) => !o);
                }}
                aria-label="Track options"
                aria-expanded={menuOpen}
                aria-haspopup="menu"
              >
                <MoreVertIcon />
              </IconButton>
              {menuOpen && (
                <MenuSurface
                  anchorEl={menuAnchor}
                  anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                  transformOrigin={{ vertical: "top", horizontal: "right" }}
                  fitBelow
                  onClose={() => setMenuOpen(false)}
                >
                  <MenuItem
                    onClick={() => {
                      setEditing(true);
                      setMenuOpen(false);
                    }}
                    component="button"
                  >
                    Edit Track
                  </MenuItem>
                  <Divider />
                  <MenuItem
                    sx={{ color: "error.main" }}
                    onClick={() => {
                      onRemove && onRemove();
                      setMenuOpen(false);
                    }}
                    component="button"
                  >
                    Delete Track
                  </MenuItem>
                </MenuSurface>
              )}
            </div>
          )}
        </div>
        {editing ? (
          <div className="track-meta">
            <div className="meta-row">
              <span>BPM</span>
              <BpmInput
                className="bpm-input"
                type="number"
                value={fields.bpm}
                aria-label="BPM"
                disabled={action.pending}
                onChange={(e) => handleChange("bpm", e.target.value)}
              />
            </div>
            <div className="meta-row">
              <span>Duration</span>
              <TextInput
                className="duration-input"
                type="text"
                value={fields.duration}
                aria-label="Duration"
                disabled={action.pending}
                onChange={(e) => handleChange("duration", e.target.value)}
              />
            </div>
          </div>
        ) : (
          <div className="track-meta">
            <div className="meta-row">
              <span>Position</span>
              <span>{track.position || "—"}</span>
            </div>
            <div className="meta-row">
              <span>BPM</span>
              {bpmInfo.hasResolved ? (
                bpmInfo.hasBadge ? (
                  <span className={`bpm-badge ${bpmInfo.badgeClass}`.trim()}>
                    {bpmInfo.displayValue}
                  </span>
                ) : (
                  <span className={bpmInfo.isDefault ? "default-bpm" : ""}>
                    {bpmInfo.displayValue}
                  </span>
                )
              ) : (
                <span>—</span>
              )}
            </div>
            <div className="meta-row">
              <span>Duration</span>
              <span>{fields.duration || "—"}</span>
            </div>
          </div>
        )}
      </div>
      {editing ? (
        <>
          <div className="edit-notes">
            <label>Notes</label>
            <TextInput
              className="notes-input"
              value={fields.notes}
              aria-label="Notes"
              disabled={action.pending}
              onChange={(e) => handleChange("notes", e.target.value)}
              multiline
              maxRows={4}
            />
          </div>
          <div className="edit-buttons">
            <Button
              type="button"
              className="btn"
              onClick={editorForm.handleSubmit(handleSave)}
              disabled={action.pending}
            >
              {action.pending ? "Saving…" : "Save"}
            </Button>
          </div>
        </>
      ) : (
        fields.notes && <div className="track-notes">{fields.notes}</div>
      )}
      {action.error && (
        <Alert role="alert" severity="error">
          {describeApiError(action.error)}
        </Alert>
      )}
    </div>
  );
}
